import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { worksSectionApi } from '../services/api';

type WorksContent = {
  badge_text: string;
  heading: string;
  description: string;
  stat1_label: string;
  stat1_value: string;
  stat2_label: string;
  stat2_value: string;
  stat3_label: string;
  stat3_value: string;
};

const FALLBACK_CONTENT: WorksContent = {
  badge_text: '3D Analysis',
  heading: 'Emprise Work Accuracy',
  description:
    'Lorem ipsum dolor sit amet, consectetur adipiscing elit. Sed do eiusmod tempor incididunt ut labore et dolore magna aliqua. Ut enim ad minim veniam, quis nostrud exercitation ullamco laboris.',
  stat1_label: 'Accuracy',
  stat1_value: '99.8%',
  stat2_label: 'Work',
  stat2_value: '60',
  stat3_label: 'Awards',
  stat3_value: '10'
};

export const SectionAI: React.FC = () => {
  const [content, setContent] = useState<WorksContent>(FALLBACK_CONTENT);

  const loadWorksSection = async () => {
    try {
      const response = await worksSectionApi.get();
      if (response?.success && response?.data) {
        setContent({
          badge_text: String(response.data.badge_text ?? FALLBACK_CONTENT.badge_text),
          heading: String(response.data.heading ?? FALLBACK_CONTENT.heading),
          description: String(response.data.description ?? FALLBACK_CONTENT.description),
          stat1_label: String(response.data.stat1_label ?? FALLBACK_CONTENT.stat1_label),
          stat1_value: String(response.data.stat1_value ?? FALLBACK_CONTENT.stat1_value),
          stat2_label: String(response.data.stat2_label ?? FALLBACK_CONTENT.stat2_label),
          stat2_value: String(response.data.stat2_value ?? FALLBACK_CONTENT.stat2_value),
          stat3_label: String(response.data.stat3_label ?? FALLBACK_CONTENT.stat3_label),
          stat3_value: String(response.data.stat3_value ?? FALLBACK_CONTENT.stat3_value)
        });
      }
    } catch {
      setContent(FALLBACK_CONTENT);
    }
  };

  useEffect(() => {
    loadWorksSection();
    const onUpdate = () => loadWorksSection();
    window.addEventListener('works:updated', onUpdate);
    return () => window.removeEventListener('works:updated', onUpdate);
  }, []);

  const stats = [
    { label: content.stat1_label, val: content.stat1_value },
    { label: content.stat2_label, val: content.stat2_value },
    { label: content.stat3_label, val: content.stat3_value }
  ];

  return (
    <section className="relative min-h-screen w-full bg-[#080808] flex items-center justify-center overflow-x-hidden py-8 md:py-0">
      <div className="absolute inset-0 z-0 bg-[radial-gradient(circle_at_top,_rgba(255,191,0,0.28)_0,_transparent_55%)]" />

      <div className="relative z-10 container mx-auto px-4 sm:px-6 text-center">
        <motion.div
          initial={{ opacity: 0, scale: 0.75, rotateX: -15 }}
          whileInView={{ opacity: 1, scale: 1, rotateX: 0 }}
          whileHover={{ rotateX: 6, rotateY: -6 }}
          transition={{ duration: 1.2, ease: 'easeOut' }}
          className="bg-black/70 backdrop-blur-2xl border border-[#FFBF00]/40 px-6 sm:px-8 md:px-10 lg:px-16 py-8 sm:py-10 md:py-14 lg:py-18 rounded-[1.5rem] sm:rounded-[2.25rem] inline-block w-full max-w-5xl shadow-[0_40px_140px_rgba(0,0,0,0.9)]"
        >
          <div className="mb-3 sm:mb-4 text-[10px] sm:text-xs md:text-sm font-black tracking-[0.3em] sm:tracking-[0.45em] uppercase text-[#FFBF00]/80">
            {content.badge_text}
          </div>

          <h2 className="text-2xl sm:text-3xl md:text-5xl lg:text-7xl font-black uppercase mb-6 sm:mb-8 md:mb-10 text-[#FFBF00] tracking-tight leading-tight">
            {content.heading}
          </h2>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 sm:gap-6 md:gap-8 mb-6 sm:mb-8 md:mb-10 text-center">
            {stats.map((stat, i) => (
              <div key={i} className="border-t border-[#FFBF00]/40 pt-4 sm:pt-5 md:pt-6">
                <p className="text-gray-400 uppercase text-[10px] sm:text-xs tracking-widest mb-1">{stat.label}</p>
                <p className="text-2xl sm:text-3xl font-bold text-white">{stat.val}</p>
              </div>
            ))}
          </div>

          <p className="text-sm sm:text-base md:text-lg text-gray-300 max-w-2xl mx-auto leading-relaxed px-2 sm:px-0">
            {content.description}
          </p>
        </motion.div>
      </div>
    </section>
  );
};
